import { Link } from "react-router-dom";
import { smoothScrollTo } from "@/lib/smoothScroll";
import { siteConfig } from "@/config";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      if (footerRef.current) {
        observer.unobserve(footerRef.current);
      }
    };
  }, []);

  const handleFooterLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();
    smoothScrollTo(href);
  };

  return (
    <footer className="py-12 border-t border-white" ref={footerRef}>
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div
            className={cn(
              "mb-6 md:mb-0 transition-all duration-700",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            )}
          >
            <Link to="/" className="font-display text-xl tracking-wider">
              {siteConfig.name.toUpperCase()}
            </Link>
          </div>

          <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-8">
            {siteConfig.navigation.map((item, index) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleFooterLinkClick(e, item.href)}
                className="text-sm uppercase tracking-wider hover:opacity-60 transition-all"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(10px)",
                  transition: "opacity 0.5s ease, transform 0.5s ease",
                  transitionDelay: `${200 + index * 100}ms`,
                }}
              >
                {item.name}
              </a>
            ))}
          </div>
        </div>

        <div className="border-t border-white/20 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p
            className="text-sm text-white/60 mb-4 md:mb-0"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(10px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
              transitionDelay: "500ms",
            }}
          >
            {siteConfig.footer.text}
          </p>

          <div className="flex space-x-6">
            <a
              href="#"
              className="text-sm text-white/60 hover:text-white transition-colors"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(10px)",
                transition: "opacity 0.5s ease, transform 0.5s ease",
                transitionDelay: "600ms",
              }}
            >
              Privacy Policy
            </a>
            <a
              href="#"
              className="text-sm text-white/60 hover:text-white transition-colors"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(10px)",
                transition: "opacity 0.5s ease, transform 0.5s ease",
                transitionDelay: "700ms",
              }}
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
